import { useMemo, useState } from "react";
import { ChevronDown, Check, Sparkles, UserCheck } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { type EquipCategory } from "@/lib/equipment";
import { useEquipmentCategories } from "@/features/settings/equipmentCategories";
import { useAllModelClassifications, useSetHumanCategory, type ModelClassRow } from "./classification";

// ============================================================================
// EQUIP-05 — Review & confirm equipment classifications. Lists the model→category
// labels the AI produced (lowest confidence first), and lets a human confirm or
// correct each one. A human pick writes source='human' which wins over the AI
// forever and is excluded from re-classification. This is how edge cases like
// Magic-Pak HWC8N ("rooftop" → should be PTAC/other) get fixed permanently with
// one click, instead of a code edit per model.
// ============================================================================

type ScopeKey = "review" | "ai" | "human" | "all";

export function ClassificationReview() {
  const { data: rows, isLoading } = useAllModelClassifications();
  // ADMIN-EDIT-01: assignable categories come from Settings → Equipment
  // categories (add one there, use it here immediately) — no more code edits.
  const { data: categories } = useEquipmentCategories();
  const assignable = (categories ?? []).filter((c) => c.active);
  const labelOf = (key: string) =>
    (categories ?? []).find((c) => c.key === key)?.label ?? key;
  const setHuman = useSetHumanCategory();
  const [scope, setScope] = useState<ScopeKey>("review");
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const counts = useMemo(() => {
    const all = rows ?? [];
    return {
      all: all.length,
      ai: all.filter((r) => r.source === "ai").length,
      human: all.filter((r) => r.source === "human").length,
      // "Needs review" = AI labels that are low-confidence or fell to "other".
      review: all.filter((r) => r.source === "ai" && ((r.confidence ?? 0) < 0.85 || r.category === "other")).length,
    };
  }, [rows]);

  const shown = useMemo(() => {
    const all = rows ?? [];
    switch (scope) {
      case "ai": return all.filter((r) => r.source === "ai");
      case "human": return all.filter((r) => r.source === "human");
      case "all": return all;
      default: return all.filter((r) => r.source === "ai" && ((r.confidence ?? 0) < 0.85 || r.category === "other"));
    }
  }, [rows, scope]);

  function confirm(r: ModelClassRow, category: EquipCategory) {
    const key = `${r.mfg_norm}|${r.model_norm}`;
    setHuman.mutate(
      { mfg: r.mfg_norm, model: r.model_norm, category },
      { onSuccess: () => { setSavedKey(key); setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1800); } },
    );
  }

  const SCOPES: [ScopeKey, string, number][] = [
    ["review", "Needs review", counts.review],
    ["ai", "AI-labeled", counts.ai],
    ["human", "Confirmed", counts.human],
    ["all", "All", counts.all],
  ];

  return (
    <div>
      {/* Scope switcher */}
      <div className="mb-4 flex flex-wrap gap-2">
        {SCOPES.map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            onClick={() => setScope(k)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ease-out-quart",
              scope === k ? "border-brand-200 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-600 hover:border-slate-300",
            )}
          >
            {label}
            <span className={cn("rounded-full px-1.5 py-0.5 text-[11px] tabular-nums", scope === k ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-500")}>{n}</span>
          </button>
        ))}
        <InfoTip className="self-center" label="How model classification works">AI labels the equipment models the built-in rules can't place. Confirm or correct any here: your pick becomes that model's label, wins over the AI and is never guessed again. Models the rules place themselves don't appear here.</InfoTip>
      </div>

      {isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading classifications…</p></Card>
      ) : shown.length === 0 ? (
        <Card className="p-10 text-center">
          <UserCheck className="mx-auto h-10 w-10 text-emerald-500" />
          <h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">Nothing to review</h3>
          <p className="mt-1 text-xs text-slate-500">Every AI label here is high-confidence or already confirmed.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Model</th>
                <th className="px-4 py-2.5">AI read</th>
                <th className="px-4 py-2.5">Category</th>
                <th className="px-4 py-2.5 text-right">Confirm</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => {
                const key = `${r.mfg_norm}|${r.model_norm}`;
                const justSaved = savedKey === key;
                return (
                  <tr key={key} className="transition-colors hover:bg-brand-50/30">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-800">{r.model_norm}</div>
                      <div className="text-[11px] text-slate-400">{r.mfg_norm}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {r.source === "human" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><UserCheck className="h-3.5 w-3.5" /> confirmed</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-600"><Sparkles className="h-3 w-3" /> {Math.round((r.confidence ?? 0) * 100)}%</span>
                        )}
                      </div>
                      {r.reason && <div className="mt-0.5 max-w-xs truncate text-[11px] text-slate-400" title={r.reason}>{r.reason}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="relative inline-block">
                        <select
                          value={r.category}
                          onChange={(e) => confirm(r, e.target.value as EquipCategory)}
                          className="h-8 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-2.5 pr-7 text-xs font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                        >
                          {/* The row's current category may be archived — keep it selectable so the select isn't lying. */}
                          {!assignable.some((c) => c.key === r.category) && (
                            <option value={r.category}>{labelOf(r.category)}</option>
                          )}
                          {assignable.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {justSaved ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span>
                      ) : r.source === "human" ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => confirm(r, r.category)}
                          className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-brand-300 hover:text-brand-700"
                        >
                          Confirm
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
